/* eslint-disable @typescript-eslint/prefer-for-of */
import React, { useMemo } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import { StatusTip, Form, Table, TableColumn, Bubble } from 'tea-component';
import SvgIcon from '@home/components/SvgIcon';
import StudioManagementMeetingEmps from '@gp/pages/StudioDashboard/StudioOverall/Tabs/Management/Details/components/Meetings/components/MeetingEmps';
import {
  getFileInfoByUrl,
  isFilePreviewableWithSuffix,
  isFileDownloadableWithSuffix,
  getNumberOfDotUnit,
} from '@gp/utils/common';
import { getAttachmentKey } from '@gp/pages/StudioDashboard/StudioOverall/Tabs/Management/Details/components/Meetings/utils';
import {
  getColumns,
  getValidMajorBodyList,
} from '@gp/pages/StudioDashboard/StudioOverall/Tabs/Management/Details/components/Meetings/components/MeetingContent/utils';
// import EllipsisTextBubble from '@gp/components/EllipsisTextBubble';

import '@gp/pages/StudioDashboard/StudioOverall/Tabs/Management/Details/components/Meetings/components/MeetingContent/index.scss';

const StudioManagementMeetingContent: React.FC<IStudioManagementMeetingContentProps> = (props) => {
  const { style, className, meeting, attachmentDownloadings, onAttachmentPreview, onAttachmentDownload } = props;

  const eventName = meeting?.eventName?.trim() || '';
  const meetingTime = meeting?.meetingTime?.trim() || '';
  const attendeeList = meeting?.attendeeList || [];
  const externalParticipants = meeting?.externalParticipants?.trim() || '';
  const majorBodyList = getValidMajorBodyList(meeting?.majorBodyList);
  const summary = meeting?.summary?.trim() || '';
  const note = meeting?.note?.trim() || '';
  const fileTagList = meeting?.fileTagList || [];

  const majorBodyListTableColumns = useMemo<TableColumn<IStudioManagementMeetingMajorBody>[]>(() => getColumns(), []);

  const meetingDate = useMemo(() => {
    if (!meetingTime) return '';
    const m = moment(meetingTime);
    return m.isValid() ? m.format('YYYY-MM-DD') : '';
  }, [meetingTime]);

  // 渲染附件列表UI
  const renderAttachmentList = (attachments: IStudioManagementMeetingFileTag[]) => (
    <ul className="dbg-studio-management-attachment-list">
      {attachments?.map((attachment, index) => {
        const { fileSuffix } = getFileInfoByUrl(attachment?.filePath);
        const attachmentKey = getAttachmentKey(meeting, attachment);
        const isAttachmentPreviewable = isFilePreviewableWithSuffix(fileSuffix);
        const isAttachmentDownloadable = isFileDownloadableWithSuffix(fileSuffix);
        const isAttachmentDownloading = attachmentDownloadings?.[attachmentKey] || false;
        return (
          <li key={index} className="dbg-studio-management-attachment-item">
            {/* 附件名称 */}
            <Bubble dark content={!isAttachmentDownloadable || isAttachmentDownloading ? null : 'Download file'}>
              <div className="">
                <span
                  className={classnames('dbg-studio-management-attachment-name', {
                    highlight: isAttachmentPreviewable || isAttachmentDownloadable,
                  })}
                  style={{
                    cursor: isAttachmentDownloading ? 'not-allowed' : 'pointer',
                  }}
                  onClick={(e) => {
                    e?.stopPropagation();
                    e?.preventDefault();
                    if (!isAttachmentDownloadable || isAttachmentDownloading) return;
                    onAttachmentDownload?.(meeting, attachment, e);
                  }}
                >
                  {attachment?.documentName}
                </span>
                {/* 下载按钮 */}

                {!isAttachmentDownloading && isAttachmentDownloadable ? (
                  <a
                    className="dbg-studio-management-attachment-operation-btn"
                    onClick={(e) => {
                      e?.stopPropagation();
                      e?.preventDefault();
                      if (!isAttachmentDownloadable) return;
                      onAttachmentDownload?.(meeting, attachment, e);
                    }}
                  >
                    <SvgIcon
                      type="download"
                      style={{
                        opacity: isAttachmentDownloadable ? 1 : 0.4,
                        cursor: isAttachmentDownloadable ? 'pointer' : 'not-allowed',
                      }}
                    />
                  </a>
                ) : null}
              </div>
            </Bubble>
            {!isAttachmentDownloading && isAttachmentPreviewable ? (
              // {/* 预览按钮 */}
              //   {/* Sorry, preview is not supported for this file type！ */}
              <Bubble dark content="Preview">
                <a
                  className="dbg-studio-management-attachment-operation-btn last-child"
                  onClick={(e) => {
                    e?.stopPropagation();
                    e?.preventDefault();
                    if (!isAttachmentPreviewable) return;
                    onAttachmentPreview?.(meeting, attachment, e);
                  }}
                >
                  <SvgIcon
                    type="open-eyes"
                    style={{
                      opacity: isAttachmentPreviewable ? 1 : 0.4,
                      cursor: isAttachmentPreviewable ? 'pointer' : 'not-allowed',
                    }}
                  />
                </a>
              </Bubble>
            ) : null}
            {isAttachmentDownloading && (
              <StatusTip
                status="loading"
                loadingText=""
                className="dbg-studio-management-attachment-downloading-icon"
              />
            )}
          </li>
        );
      })}
    </ul>
  );

  /**
   * 处理董事会议 表单字段左右对齐样式问题
   */
  (() => {
    const formNodes = document.querySelectorAll('.dbg-studio-management-meeting-form');
    if (!formNodes) return;

    const updateInlineFormItemsNodesClassList = (formItemsNodes: NodeListOf<Element>) => {
      if (!formItemsNodes) return;
      for (let j = 0;j < formItemsNodes.length;j++) {
        const formItemNode = formItemsNodes[j];
        if ((j + 1) % 2 === 0) {
          formItemNode.classList.add('no-mr');
        } else {
          formItemNode.classList.remove('no-mr');
        }
      }
    };

    for (let i = 0;i < formNodes.length;i++) {
      const formNode = formNodes[i];
      if (!formNode) continue;
      const inlineFormItemsNodes = formNode.querySelectorAll('.dbg-studio-management-form-item:not(.block-item)');
      updateInlineFormItemsNodesClassList(inlineFormItemsNodes);
    }
  })();

  // 根据数据动态计算label的统一宽度
  // const labelClassName = useMemo(() => {
  //   if (summary) return 'dbg-studio-management-form-item174';
  //   // if (externalParticipants) return 'dbg-studio-management-form-item130';
  //   if (attendeeList?.length > 0) return 'dbg-studio-management-form-item124';
  //   if (fileTagList?.length > 0) return 'dbg-studio-management-form-item115';
  //   if (eventName) return 'dbg-studio-management-form-item73';
  //   return 'dbg-studio-management-form-item40';
  // }, [
  //   eventName,
  //   // meetingDate,
  //   attendeeList,
  //   // externalParticipants,
  //   summary,
  //   majorBodyList,
  //   note,
  //   fileTagList,
  // ]);

  return (
    <div style={style} className={classnames('dbg-studio-management-meeting-content', className)}>
      <div className="dbg-studio-management-meeting-section">
        <div className="dbg-studio-management-meeting-section-title">Meeting Content</div>
        <Form className="dbg-studio-management-meeting-form">
          {eventName && (
            <Form.Item
              label="Event Type"
              align="top"
              className={`dbg-studio-management-form-item dbg-studio-management-form-item-col `}
            >
              <Form.Text className="dbg-studio-management-form-text dbg-word-break-word">{eventName}</Form.Text>
            </Form.Item>
          )}
          {meetingDate && (
            <Form.Item
              label="Meeting/Resolution Time"
              align="top"
              className="dbg-studio-management-form-item dbg-studio-management-form-item-col"
            >
              <Form.Text className="dbg-studio-management-form-text">{meetingDate}</Form.Text>
            </Form.Item>
          )}
          {attendeeList?.length > 0 && (
            <Form.Item
              label="Tencent Attendees"
              // align="middle"
              className={`dbg-studio-management-form-item label-top-14 dbg-studio-management-form-item-col dbg-word-break-word `}
            >
              <StudioManagementMeetingEmps emps={attendeeList} />
            </Form.Item>
          )}
          {externalParticipants && (
            <Form.Item
              label="External Attemdees"
              align="top"
              className="dbg-studio-management-form-item dbg-studio-management-form-item-col"
              // className={`dbg-studio-management-form-item ${labelClassName}`}
            >
              <Form.Text className="dbg-studio-management-form-text dbg-word-break-word">
                {externalParticipants}
              </Form.Text>
            </Form.Item>
          )}
          {summary || majorBodyList?.length > 0 ? (
            <Form.Item
              label="Meeting Agenda/Minutes"
              align="top"
              className={`dbg-studio-management-form-item block-item `}
            >
              {summary ? (
                <Form.Text className="dbg-studio-management-form-text dbg-word-break-word">
                  <div className="form-text margin-text font14">{summary}</div>
                </Form.Text>
              ) : null}
              {majorBodyList?.length > 0 ? (
                <Table
                  className="dbg-common-table dbg-studio-management-meeting-major-body-list-table"
                  columns={majorBodyListTableColumns}
                  recordKey={(record) => String(record?.majorId)}
                  records={majorBodyList}
                />
              ) : null}
            </Form.Item>
          ) : null}
          {/* {majorBodyList?.length > 0 && (
            <Form.Item
              label="Meeting Agenda/Minutes"
              align="top"
              className={`dbg-studio-management-form-item block-item `}
            >
              <Table
                className="dbg-common-table dbg-studio-management-meeting-major-body-list-table"
                columns={majorBodyListTableColumns}
                recordKey={(record) => String(record?.majorId)}
                records={majorBodyList}
              />
            </Form.Item>
          )} */}
          {note && (
            <Form.Item label="Notes" align="top" className={`dbg-studio-management-form-item `}>
              <Form.Text className="dbg-studio-management-form-text dbg-word-break-word">
                <div className="form-text font14">{note || '-'}</div>
              </Form.Text>
            </Form.Item>
          )}
        </Form>
      </div>
      {fileTagList?.length > 0 && (
        <div className="dbg-studio-management-meeting-section">
          <div className="dbg-studio-management-meeting-section-title">Meeting Content</div>
          <Form className="dbg-studio-management-meeting-form" layout="inline">
            <Form.Item
              label={`Meeting Minutes (${getNumberOfDotUnit(fileTagList?.length)})`}
              align="top"
              className={`dbg-studio-management-form-item block-item no-mt dbg-word-break-word`}
            >
              {renderAttachmentList(fileTagList)}
            </Form.Item>
          </Form>
        </div>
      )}
    </div>
  );
};

export default React.memo(StudioManagementMeetingContent);
